import React, { useState } from 'react';
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AddIcon from '@mui/icons-material/Add';
import { useBrand } from 'contexts/BrandContext';
import { useWhiteLabel } from 'contexts/WhiteLabelContext';
import { BrandLogo } from 'dashboard/components/common';
import CreateModal from 'dashboard/components/brand/create';
import Logo from 'dashboard/components/sidebar/components/Logo';

/**
 * Top of the sidebar: which brand this is, and the way to change it.
 *
 * Switching brands used to live in the navbar as well as here, so the same
 * brand name sat in two places on one screen and only one of them did
 * anything. The sidebar is the right home for it: everything below is scoped
 * to the brand, so the thing that sets that scope belongs at the top of the
 * list it governs, not floating opposite it.
 *
 * Falls back to the platform mark before the first brand exists. The
 * platform-admin console keeps the TechProOne mark — that surface really is
 * the platform, not a brand.
 *
 * On a brand's own domain it is not a switcher at all, just the brand's name.
 * There is one brand there and it is not the viewer's to change; a control
 * that opens a menu of one, or worse a menu naming brands they happen to own
 * elsewhere, would be telling the bakery's staff about the bike shop.
 */
export default function SidebarBrandMark({ expanded = true }) {
  const { brandList, selectedBrand, setSelectedBrand, fetchBrands } = useBrand();
  const whiteLabel = useWhiteLabel();
  const [anchorEl, setAnchorEl] = useState(null);
  const [creating, setCreating] = useState(false);

  const brands = (brandList || []).map((b) => b.brand_id).filter(Boolean);
  const current = brands.find((b) => b._id === selectedBrand);

  // Before a brand resolves, the platform mark — except on a brand's own
  // domain, where our mark must never appear. Nothing is better than
  // somebody else's logo on your dashboard.
  if (!current) return whiteLabel.active ? null : <Logo expanded={expanded} />;

  const name = current.name || '';

  if (whiteLabel.active) {
    return (
      <Stack direction='row' alignItems='center' spacing='10px' sx={{ minWidth: 0, width: '100%', p: '6px' }}>
        <BrandLogo src={current.logo_upload_path} name={name} size={34} rounded={false} />
        {expanded && (
          <Typography
            variant='subtitle1' fontWeight='800' letterSpacing='-0.2px' noWrap
            title={name} sx={{ flex: 1, minWidth: 0 }}
          >
            {name}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <>
      <Stack
        component='button'
        type='button'
        onClick={(e) => setAnchorEl(e.currentTarget)}
        direction='row'
        alignItems='center'
        spacing='10px'
        aria-haspopup='true'
        aria-label={`Current brand: ${name}. Switch brand`}
        sx={{
          minWidth: 0, width: '100%',
          background: 'none', border: 'none', font: 'inherit',
          textAlign: 'left', cursor: 'pointer',
          p: '6px', mx: '-6px', borderRadius: '10px',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {/* 34px, so a brand with no logo shows its initials — "NO LOGO" is not
            legible at this size (see BrandLogo). */}
        <BrandLogo src={current.logo_upload_path} name={name} size={34} rounded={false} />
        {expanded && (
          <>
            <Typography
              variant='subtitle1'
              fontWeight='800'
              letterSpacing='-0.2px'
              noWrap
              title={name}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {name}
            </Typography>
            <UnfoldMoreIcon fontSize='small' sx={{ color: 'text.secondary', flexShrink: 0 }} />
          </>
        )}
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240, borderRadius: '12px', mt: '4px' } } }}
      >
        {brands.map((b) => (
          <MenuItem
            key={b._id}
            selected={b._id === selectedBrand}
            onClick={() => { setSelectedBrand(b._id); setAnchorEl(null); }}
          >
            <Stack direction='row' alignItems='center' spacing='10px' sx={{ minWidth: 0 }}>
              <BrandLogo src={b.logo_upload_path} name={b.name} size={24} rounded={false} />
              <Typography variant='body2' noWrap>{b.name}</Typography>
            </Stack>
          </MenuItem>
        ))}

        <Divider sx={{ my: '4px' }} />

        <MenuItem onClick={() => { setAnchorEl(null); setCreating(true); }}>
          <Stack direction='row' alignItems='center' spacing='10px'>
            <Box
              sx={{
                width: 24, height: 24, borderRadius: '6px',
                border: '1px dashed', borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <AddIcon sx={{ fontSize: 15 }} />
            </Box>
            <Typography variant='body2'>New brand</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      {creating && (
        <CreateModal
          add
          fetchBrands={fetchBrands}
          setShowCreateModal={setCreating}
          onBrandCreated={(id) => { setCreating(false); fetchBrands(id); }}
        />
      )}
    </>
  );
}
